import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/components/Footer.css';
import logo from '../assets/images/logo/companylogo.png';
import facebookIcon from '../assets/social/facebook.png';
import instagramIcon from '../assets/social/instagram.png';
import whatsappIcon from '../assets/social/whatsapp.png';
// Import new PNG icons
import phoneIcon from '../assets/social/phone-call.png';
import whatsappChatIcon from '../assets/social/whatsapp (1).png';
import emailIcon from '../assets/social/gmail.png';
import locationIcon from '../assets/social/3d-map.png';
import contactFormIcon from '../assets/social/contact-form.png';
import callIcon from '../assets/social/phone-call.png';
import copyIcon from '../assets/social/copyIcon.png';
// Import background image
import footerBackground from '../assets/Footer/footer1.jpg';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [activePopup, setActivePopup] = useState(null);

  const footerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const socialLinks = [
    { 
      name: 'Facebook', 
      icon: facebookIcon, 
      url: 'https://facebook.com/meezandevelopers', 
    },
    { 
      name: 'Instagram', 
      icon: instagramIcon, 
      url: 'https://instagram.com/meezandevelopers', 
    },
    { 
      name: 'WhatsApp', 
      icon: whatsappIcon, 
      url: 'https://wa.me/+923218836371', 
    }
  ];

  const contactMethods = [
    {
      id: 'call',
      icon: phoneIcon,
      title: 'Call Us',
      description: 'Speak directly with our team',
      details: [
        { label: 'Primary Number', value: '0321 88 36 37 1' },
        { label: 'Secondary Number', value: '03 111 786 646' }
      ],
      action: 'tel:+923218836371',
      actionText: 'Call Now',
    },
    {
      id: 'whatsapp',
      icon: whatsappChatIcon,
      title: 'WhatsApp',
      description: 'Quick chat for instant support',
      details: [
        { label: 'Primary WhatsApp', value: '03 111 786 646' },
        { label: 'Secondary WhatsApp', value: '0321 88 36 37 1' }
      ],
      action: 'https://wa.me/+923111786646',
      actionText: 'Start Chat',
    },
    {
      id: 'email',
      icon: emailIcon,
      title: 'Email Us',
      description: 'Send us your detailed inquiries',
      details: [
        { label: 'Email Address', value: 'info@meezandevelopers.com' },
      ],
      action: 'mailto:info@meezandevelopers.com',
      actionText: 'Send Email',
    }
  ];

  const openPopup = (popupId) => {
    setActivePopup(popupId);
  };

  const closePopup = () => {
    setActivePopup(null);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    });
  };

  return (
    <motion.footer 
      className="footer professional-footer"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={footerVariants}
    >
      <div className="footer-background-overlay">
        <div 
          className="footer-background-image"
          style={{
            backgroundImage: `url(${footerBackground})`
          }}
        ></div>
      </div>
      
      <div className="container">
        {/* Main Horizontal Footer */}
        <div className="footer-horizontal-main">
          {/* Left Section - Contact Methods & Social */}
          <motion.div 
            className="footer-left-section"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Contact Methods with Popup */}
            <div className="contact-methods-grid">
              {contactMethods.map((method) => (
                <motion.div
                  key={method.id}
                  className="contact-method-card"
                  onClick={() => openPopup(method.id)}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="method-icon-container">
                    <div className="method-icon-3d">
                      <img src={method.icon} alt={method.title} className="method-icon-img" />
                    </div>
                  </div>
                  <div className="method-info">
                    <span className="method-title">{method.title}</span>
                    <span className="method-desc">{method.description}</span>
                  </div>
                  <div className="method-arrow">→</div>
                </motion.div>
              ))}
            </div>

            {/* Social Icons */}
            <div className="social-section">
              <h5 className="social-title">Follow Our Work</h5>
              <div className="social-icons-row">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    className="social-icon-link-3d"
                    aria-label={social.name}
                    whileHover={{ 
                      scale: 1.1,
                      y: -2
                    }}
                    whileTap={{ scale: 0.95 }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="social-icon-3d">
                      <img 
                        src={social.icon} 
                        alt={social.name}
                        className="social-icon-img"
                      />
                    </div>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right Section - Quick Actions */}
          <motion.div 
            className="footer-right-section"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Quick Actions */}
            <div className="action-buttons-pro">
              <motion.a 
                href="/contact"
                className="action-btn-pro primary-btn"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="btn-icon-3d">
                  <img src={contactFormIcon} alt="Contact Form" className="btn-icon-pro-img" />
                </div>
                <span className="btn-text-pro">
                  <span className="btn-main-text">Contact Form</span>
                  <span className="btn-sub-text">Detailed Inquiry</span>
                </span>
              </motion.a>

              <motion.a 
                href="https://wa.me/+923111786646"
                className="action-btn-pro whatsapp-btn"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="btn-icon-3d">
                  <img src={whatsappIcon} alt="WhatsApp" className="btn-icon-pro-img" />
                </div>
                <span className="btn-text-pro">
                  <span className="btn-main-text">WhatsApp</span>
                  <span className="btn-sub-text">Instant Support</span>
                </span>
              </motion.a>

              <motion.a 
                href="tel:+923218836371"
                className="action-btn-pro call-btn"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="btn-icon-3d">
                  <img src={callIcon} alt="Call" className="btn-icon-pro-img" />
                </div>
                <span className="btn-text-pro">
                  <span className="btn-main-text">Call Now</span>
                  <span className="btn-sub-text">Direct Line</span>
                </span>
              </motion.a>
            </div>

            <div className="location-pro">
              <div className="location-icon-3d">
                <img src={locationIcon} alt="Location" className="location-icon-img" />
              </div>
              <div className="location-details">
                <span className="location-label">Visit Our Office</span>
                <span className="location-address">
                  103-C, Etihad Town, Raiwind Road, Lahore
                </span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Footer Bottom Bar */}
        <motion.div 
          className="footer-bottom-bar"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="footer-bottom-content">
            <p className="copyright-text">
              © {currentYear} <strong>Meezan Developers</strong>. All rights reserved.
            </p>
            <div className="legal-links">
              <strong className="legal-link">Privacy Policy</strong>
              <span className="divider">|</span>
              <strong className="legal-link">Terms of Service</strong>
              <span className="divider">|</span>
              <strong className="legal-link">Cookie Policy</strong>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Contact Method Popups */}
      <AnimatePresence>
        {activePopup && (
          <motion.div 
            className="contact-popup-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closePopup}
          >
            <motion.div 
              className="contact-popup"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {contactMethods.map((method) => (
                method.id === activePopup && (
                  <div key={method.id} className="popup-content">
                    <div className="popup-header">
                      <div className="popup-icon-3d">
                        <img src={method.icon} alt={method.title} className="popup-icon-img" />
                      </div>
                      <div className="popup-title">
                        <h3>{method.title}</h3>
                        <p>{method.description}</p>
                      </div>
                      <button className="close-popup" onClick={closePopup}>×</button>
                    </div>
                    
                    <div className="popup-details">
                      {method.details.map((detail, index) => (
                        <div key={index} className="detail-item-multiple">
                          <span className="detail-label">{detail.label}</span>
                          <span className="detail-value-multiple">{detail.value}</span>
                          <button 
                            className="detail-copy-btn" 
                            onClick={() => copyToClipboard(detail.value)}
                          >
                            <img src={copyIcon} alt="Copy" className="copy-icon-small" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="popup-actions">
                      <motion.a
                        href={method.action}
                        className="popup-action-btn"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        target={method.id === 'whatsapp' ? '_blank' : '_self'}
                      >
                        {method.actionText}
                      </motion.a>
                      <button 
                        className="popup-copy-btn" 
                        onClick={() => copyToClipboard(method.details[0].value)}
                      >
                        <div className="copy-icon-3d">
                          <img src={copyIcon} alt="Copy" className="copy-icon" />
                        </div>
                        Copy {method.id === 'email' ? 'Email' : 'Number'}
                      </button>
                    </div>
                  </div>
                )
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Professional Background Elements */}
      <div className="footer-pattern"></div>
      <div className="footer-accent"></div>
    </motion.footer>
  );
};

export default Footer;